import { useCallback, useMemo } from "react";

import { getCurrentUser } from "../services/authService";
import { Project, User } from "../utils/models";
import { formatTime, getInternalReviewDetails, getIsAfter } from "../utils/utilities";
import { APP_DATE_FORMAT } from "../utils/constants";
import { BONGA_FPSO_ID } from "../utils/ids";

import config from "../utils/config";

const useInspectorDetails = (projectDetails: Project) => {
    const user = getCurrentUser() as User;
	
	const isSupervisor = user.id === projectDetails?.supervisorId;
	const userFullName = user.firstName + ' ' + user.lastName;

    const getDate = useCallback((date: string | Date): string => {
		return date ? formatTime(date, APP_DATE_FORMAT) : '';
	}, []);

	const approverName = useMemo(() => {
		if (projectDetails.facilityId === BONGA_FPSO_ID) {
			return 'Shell Nigeria Exploration and Production Company (SNEPCO)'
		}

		return projectDetails.organisationName;
	}, [projectDetails]);

    const reviewerInfo = useMemo(() => {
		return getInternalReviewDetails(projectDetails)
	}, [projectDetails]);

    const inspectorQualifications = useMemo(() => {
		const FVI_AND_UTM_REPORTS = [
			config.supportedTemplates.exxonMobilFVIEquipment,
			config.supportedTemplates.exxonMobilFVIPiping,
			config.supportedTemplates.exxonMobilFVIStructure,
			config.supportedTemplates.exxonMobilUTMEquipment,
			config.supportedTemplates.exxonMobilUTMPiping,
			config.supportedTemplates.exxonMobilUTMStructure,
		];

		const PEC_REPORTS = [
			config.supportedTemplates.exxonMobilPEC,
			config.supportedTemplates.pec,
		];

		if (FVI_AND_UTM_REPORTS.includes(projectDetails.inspectionTypeId)) {
			return 'NDT Inspector / Ultrasonic Test - Level 2 / Team Lead';
		}

		if (PEC_REPORTS.includes(projectDetails.inspectionTypeId)) {
			return 'PEC Inspector /  ETLevel 2 / Team Lead'
		}

		if (projectDetails.inspectionTypeId === config.supportedTemplates.exxonMobilDRT) {
			return 'NDT Level 2/ DRT Inspector';
		}
		
		if (projectDetails.inspectionTypeId === config.supportedTemplates.pautNew) {
			return 'NDT Level 2/ PAUT Inspector';
		}

		return isSupervisor ? user.designation : projectDetails.supervisorDesignation;
	}, [projectDetails.inspectionTypeId, projectDetails.supervisorDesignation, user.designation, isSupervisor]);

    const inspectionDate = useMemo(() => {
		if (projectDetails?.report?.updatedAt && reviewerInfo.reviewDate) {
			return getIsAfter(projectDetails.report.updatedAt, reviewerInfo.reviewDate) ?  getDate(reviewerInfo.reviewDate) : getDate(projectDetails.report.updatedAt); 
		}

		return getDate(new Date());
	}, [projectDetails?.report, reviewerInfo, getDate]);

    const approverInfo = useMemo(() => {
		return {
			approverName: `${approverName} Representative: Name, Date & Signature`,
			approverQualifications: projectDetails.report.acceptedByDesignation,
			approvalDate: getDate(projectDetails.report.acceptedAt),
			approverSignature: projectDetails.report.acceptedBySignature,
		}
	}, [approverName, projectDetails.report, getDate]);

    return { 
        inspectorSignature: projectDetails.report?.createdBySignature ?? projectDetails.supervisorSignature,
        inspectorName: projectDetails.report?.creatorName ?? projectDetails.supervisorName,
        isSupervisor, 
        userFullName, 
        inspectorQualifications, 
        inspectionDate, 
        reviewerInfo, 
        approverInfo 
    };
};

export default useInspectorDetails;